import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useGetPaymentSettingsQuery } from '../hooks/useGetPaymentSettingsQuery';

interface PaymentSettings {
  paymentEnabled: boolean;
  testMode: boolean;
  testModeMessage: string;
}

interface PaymentSettingsContextType {
  settings: PaymentSettings;
  updateSettings: (newSettings: Partial<PaymentSettings>) => void;
  isPaymentEnabled: () => boolean;
  isTestMode: () => boolean;
  isLoading: boolean;
  hasUnsavedChanges: boolean;
}

const defaultSettings: PaymentSettings = {
  paymentEnabled: false, // Start with payments disabled for beta
  testMode: true,
  testModeMessage: "Beta Mode - No charges will be applied"
};

const PaymentSettingsContext = createContext<PaymentSettingsContextType | undefined>(undefined);

interface PaymentSettingsProviderProps {
  children: ReactNode;
}

export const PaymentSettingsProvider: React.FC<PaymentSettingsProviderProps> = ({ children }) => {
  const { paymentSettings, loading, refetch } = useGetPaymentSettingsQuery();
  const [localSettings, setLocalSettings] = useState<PaymentSettings>(defaultSettings);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Update local settings when backend data is loaded
  useEffect(() => {
    if (paymentSettings) {
      setLocalSettings({
        paymentEnabled: paymentSettings.paymentEnabled ?? defaultSettings.paymentEnabled,
        testMode: paymentSettings.testMode ?? defaultSettings.testMode,
        testModeMessage: paymentSettings.testModeMessage || defaultSettings.testModeMessage,
      });
      setHasUnsavedChanges(false);
    }
  }, [paymentSettings]);

  const updateSettings = (newSettings: Partial<PaymentSettings>) => {
    setLocalSettings(prev => ({ ...prev, ...newSettings }));
    setHasUnsavedChanges(true);
  };

  const isPaymentEnabled = () => localSettings.paymentEnabled;
  const isTestMode = () => localSettings.testMode;

  const contextValue: PaymentSettingsContextType = {
    settings: localSettings,
    updateSettings,
    isPaymentEnabled,
    isTestMode,
    isLoading: loading,
    hasUnsavedChanges,
  };

  return (
    <PaymentSettingsContext.Provider value={contextValue}>
      {children}
    </PaymentSettingsContext.Provider>
  );
};

export const usePaymentSettings = (): PaymentSettingsContextType => {
  const context = useContext(PaymentSettingsContext);
  if (context === undefined) {
    throw new Error('usePaymentSettings must be used within a PaymentSettingsProvider');
  }
  return context;
};
